import {AddressProps, translatedAddressProps} from 'modules/addresses/types';
import {CompanyProps, translatedCompanyProps} from 'modules/companies/types';
import {
  CouponProps,
  SponsorTypeEnum,
  translatedCouponProps,
} from 'modules/coupons/types';
import {
  PaymentMethodProps,
  translatedPaymentMethodProps,
} from 'modules/payment-methods/types';
import {
  AdditionalGroupConfigTypeEnum,
  ProductProps,
} from 'modules/products/types';
import {translatedUserProps, UserProps} from 'modules/users/types';
import {TransactionRecordProps} from 'modules/wallet/types';
import {TranslatedKeys} from 'shared/types/translated-keys';

export enum OrderStatus {
  awaiting_payment = 'awaiting_payment',
  opened = 'opened',
  preparing = 'preparing',
  ready_to_pickup = 'ready_to_pickup',
  ready_to_delivery = 'ready_to_delivery',
  delivering = 'delivering',
  arrival = 'arrival',
  delivery_man_assigned = 'delivery_man_assigned',
  delivery_man_auto_assigned = 'delivery_man_auto_assigned',
  finished = 'finished',
  canceled = 'canceled',
}

export enum OrderTypeEnum {
  delivery = 'delivery',
  pickup = 'pickup',
}

export enum OrderOriginEnum {
  organic = 'organic',
  guest = 'guest',
  system = 'system',
}

export interface GuestUserProps {
  name: string;
  phone: string;
  email?: string;
  document?: string;
}

export interface OrderStatuses {
  user: string;
  name: OrderStatus;
  date: Date;
  deliveryMan: string;
}

export interface OrderAdditionalProps {
  id: string;
  name: string;
  quantity: number;
  price?: number;
}

export interface OrderAdditionalGroupProps {
  id: string;
  additionalGroupId: string;
  min: number;
  max: number;
  isFree: boolean;
  type: AdditionalGroupConfigTypeEnum;
  additionals: OrderAdditionalProps[];
  total: number;
}

export interface OrderProductProps extends ProductProps {
  salePrice: number;
  quantity: number;
  size: string;
  additionalGroups: OrderAdditionalGroupProps[];
  observation?: string;
}

export interface BillingDetailsProps {
  // ORDER
  rawTotal: number;
  consideredTotal: number;

  // DELIVERIES
  paidDeliveryPrice: number;
  freeDeliveryPrice: number;
  totalDeliveryPrice: number;

  // COMPANY
  companyCouponDiscount: number;
  companyProductsDiscount: number;
  companyFreeDeliveryPrice: number;
  retainedEarningsAmount: number;
  companyAmount: number;

  // CITY
  cityCouponDiscount: number;
  cityProductsDiscount: number;
  cityFreeDeliveryPrice: number;
  cityRawAmount: number;
  cityAmount: number;

  // BALANCE
  balanceAmount: number;

  // FRANCHISE
  franchiseAmount: number;
}

export interface OrderAddressProps
  extends Omit<AddressProps, 'neighborhood' | 'city'> {
  neighborhood: string;
  city: string;
}

export interface OrderProps extends BillingDetailsProps {
  id: string;
  companyId: string;
  company: CompanyProps;
  userId: string;
  user: UserProps;
  document?: string;
  number: number;
  deliveryPrice: number;
  total: number;
  couponId?: string;
  coupon?: CouponProps;
  paymentId: string;
  payment: PaymentMethodProps;
  addressId: string;
  address?: OrderAddressProps;
  products: OrderProductProps[];
  type: OrderTypeEnum;
  statuses: OrderStatuses[];
  currentStatus: OrderStatuses;
  currentStatusName: OrderStatus;
  createdAt: Date;
  updatedAt: Date;
  guestUser?: GuestUserProps;
  origin: OrderOriginEnum;
  deliveryManId?: string;
  deliveryMan?: UserProps;
  deliveryManFee: number;
  polyline: string;
  observation?: string;
  discountRecord?: TransactionRecordProps;
  freeDelivery?: boolean;
  retainedEarnings?: boolean;
  paidTotal: number;
  couponTotal?: number;
  balanceTotal?: number;
  changeSent?: boolean;
  deliveryManName?: string;
  userName?: string;
  userPhone?: string;
  cityName?: string;
  companyName?: string;
}

export const translatedOrderProps: TranslatedKeys<OrderProps> = {
  companyId: 'Empresa',
  company: {...translatedCompanyProps, name: 'Empresa'},
  userId: 'Usuário',
  user: {...translatedUserProps, name: 'Usuário'},
  document: 'Cpf/Cnpj',
  number: 'Id',
  deliveryPrice: 'Taxa de entrega',
  total: 'Total',
  couponId: 'Cupom',
  coupon: {...translatedCouponProps, name: 'Cupom'},
  paymentId: 'Forma de pagamento',
  payment: {...translatedPaymentMethodProps, name: 'Forma de pagamento'},
  addressId: 'Endereço',
  address: {
    ...translatedAddressProps,
    name: 'Endereço',
    city: 'Cidade',
    neighborhood: 'Bairro',
  },
  products: 'Produtos',
  type: 'Tipo',
  statuses: 'Histórico',
  currentStatus: {
    user: 'Usuário',
    date: 'Data',
    name: 'Status',
    deliveryMan: 'Entregador',
  },
  currentStatusName: 'Status',
  createdAt: 'Data do pedido',
  updatedAt: 'Atualizado em',
  guestUser: {...translatedUserProps, name: 'Cliente'},
  origin: 'Origem do pedido',
  deliveryManId: 'Entregador',
  deliveryMan: {...translatedUserProps, name: 'Entregador'},
  deliveryManFee: 'Taxa do entregador',
  polyline: 'Rota',
  observation: 'Observação',
  freeDelivery: 'Entrega grátis',
  retainedEarnings: 'Dinheiro com a empresa?',
  paidTotal: 'Valor pago',
  changeSent: 'Troco enviado?',
  deliveryManName: 'Entregador',
  userName: 'Usuário',
  userPhone: 'Telefone',
  cityName: 'Cidade',
  companyName: 'Empresa',

  // ORDER
  rawTotal: 'Total bruto',
  consideredTotal: 'Total considerado',

  // DELIVERIES
  paidDeliveryPrice: 'Valor pago da entrega',
  freeDeliveryPrice: 'Valor em entrega grátis',
  totalDeliveryPrice: 'Taxa de entrega',

  // COMPANY
  companyCouponDiscount: 'Cupons empresa',
  companyProductsDiscount: 'Desconto em produtos empresa',
  companyFreeDeliveryPrice: 'Entrega grátis empresa',
  retainedEarningsAmount: 'Dinheiro com a empresa',
  companyAmount: 'Repasse empresa',

  // CITY
  cityCouponDiscount: 'Cupons cidade',
  cityProductsDiscount: 'Desconto em produtos cidade',
  cityFreeDeliveryPrice: 'Entrega grátis cidade',
  cityRawAmount: 'Valor cidade bruto',
  cityAmount: 'Valor cidade',

  // BALANCE
  balanceAmount: 'Carteira',

  // FRANCHISE
  franchiseAmount: 'Valor franquia',
};

export enum OccurenceTargetEnum {
  CITY = 'CITY',
  'COMPANY' = 'COMPANY',
  'DELIVERY_MAN' = 'DELIVERY_MAN',
}

export interface OccurrenceProps {
  id: string;
  value: number;
  description: string;
  observation?: string;
  orderId: string;

  payer?: OccurenceTargetEnum;
  receiver?: OccurenceTargetEnum;
  deliveryManId?: string;
  deliveryMan?: UserProps;

  createdAt: Date;
  updatedAt: Date;
}

export const translatedOccurrenceProps: TranslatedKeys<OccurrenceProps> = {
  value: 'Valor',
  description: 'Descrição',
  observation: 'Observação',
  payer: 'Quem paga',
  receiver: 'Quem recebe',
  deliveryManId: 'Entregador',
  deliveryMan: {...translatedUserProps, name: 'Entregador'},
  createdAt: 'Criado em',
  updatedAt: 'Atualizado em',
  orderId: 'Order id',
};
